import { Pencil, Trash2, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { memo, ReactNode } from 'react'

import { Button, ButtonProps, SimpleTooltip } from '@fp/ui-kit'
import { ConfirmActionButton } from '@ui/ConfirmActionButton'

export type TableAction = {
  key: string
  content: ReactNode
  href?: string
  tooltip?: string
}

type TableActionsProps = {
  actions: TableAction[]
  className?: string
}

export const TableActions = memo(({ actions, className }: TableActionsProps) => {
  return (
    <div className={`flex w-fit gap-0.5 ${className || ''}`}>
      {actions.map((action) => {
        const content = action.tooltip ? (
          <SimpleTooltip content={action.tooltip}>{action.content}</SimpleTooltip>
        ) : (
          action.content
        )

        return action.href ? (
          <Link key={action.key} href={action.href}>
            {content}
          </Link>
        ) : (
          <div key={action.key}>{content}</div>
        )
      })}
    </div>
  )
})

TableActions.displayName = 'TableActions'

const defaultButtonProps: ButtonProps = {
  variant: 'outline',
  size: 'sm',
}

export const createViewAction = (href: string, buttonProps?: ButtonProps): TableAction => ({
  key: 'view',
  href,
  tooltip: 'View details',
  content: (
    <Button {...defaultButtonProps} {...buttonProps}>
      <ChevronRight />
    </Button>
  ),
})

export const createEditAction = (onClick: () => void, buttonProps?: ButtonProps): TableAction => ({
  key: 'edit',
  tooltip: 'Edit',
  content: (
    <Button {...defaultButtonProps} {...buttonProps} onClick={onClick}>
      <Pencil />
    </Button>
  ),
})

export const createShowAction = (onClick: () => void, buttonProps?: ButtonProps): TableAction => ({
  key: 'show',
  tooltip: 'View details',
  content: (
    <Button {...defaultButtonProps} {...buttonProps} onClick={onClick}>
      <ChevronRight />
    </Button>
  ),
})

export const createDeleteAction = (
  itemName: string,
  onConfirm: () => void,
  buttonProps?: ButtonProps,
): TableAction => ({
  key: 'delete',
  content: (
    <ConfirmActionButton
      title='Delete Item'
      button={
        <Button {...defaultButtonProps} {...buttonProps}>
          <Trash2 />
        </Button>
      }
      message={`Are you sure you want to delete ${itemName}?`}
      onConfirm={onConfirm}
      tooltipText='Delete'
    />
  ),
})

export const createCustomAction = (
  key: string,
  content: ReactNode,
  options?: {
    href?: string
    tooltip?: string
  },
): TableAction => ({
  key,
  content,
  ...options,
})
